// EsgMonitorBanner — publisher-facing ESG transmission + ACK display.
//
// Combines three feature streams:
//
//   TODO-104 (this phase, ACK receipt display): reads
//     EsgSubmittedAt / EsgAckReceivedAt / EsgAckTimestamp / EsgStaleAt /
//     EsgAckPayload from the Submission row and renders one of 4 states:
//       • not transmitted      → component returns null (no banner)
//       • awaiting ACK         → blue banner
//       • ACK overdue (stale)  → amber banner + advice copy
//       • ACK received         → green banner + Download ACK button
//
//   TODO-105 (transmission status): envelope id, transmission size,
//     retry count surfaced when EsgEnvelopeId / EsgTransmissionSizeBytes
//     / EsgRetryCount are populated.
//
//   TODO-106 (retry on error): a "Retry transmission" button is exposed
//     when EsgLastError is set, or when ACK is still pending and the
//     caller provides a submissionId.
//
// Two API surfaces are supported for backwards-compat with both lines:
//
//   • New: pass the full `submission` prop — drives all of TODO-104 + 105
//     + 106 automatically.
//   • Legacy positional props (`agencyReferenceNumber`, `submittedAt`,
//     `envelopeId`, `transmissionSizeBytes`, `retryCount`, `lastError`,
//     `ackPending`, `onRetried`, `submissionId`) — preserved unchanged.

import { useState, type ReactElement } from 'react';
import type { Submission } from '@/api/v2Client';
import { v2, ApiError } from '@/api/v2Client';
import { formatDateTime } from '@/lib/format';

export interface EsgMonitorBannerProps {
  /**
   * NEW (TODO-104): the full submission row — when supplied, drives the
   * 4-state ACK display and supersedes most legacy positional props.
   */
  readonly submission?: Submission;

  // ── Legacy positional API (back-compat, TODO-105/106 wiring) ───────────
  readonly agencyReferenceNumber?: string;
  readonly submissionId?: string | null;
  readonly submittedAt?: string | null;
  readonly envelopeId?: string | null;
  readonly transmissionSizeBytes?: number | null;
  readonly retryCount?: number | null;
  readonly lastError?: string | null;
  /** When true, ack has not yet been received from the gateway. */
  readonly ackPending?: boolean;
  /**
   * Called after a successful retry so the parent can refresh the
   * submission data and the banner re-renders against the new state.
   */
  readonly onRetried?: () => void;
  /**
   * Phase settings-monitor-esg-ack (TODO-074 partial) — when `false`,
   * the banner shows a "Manual ACK check required" note + "Check ACK
   * status" button instead of waiting for the cron. Defaults to true
   * so unchanged callers keep the prior behaviour.
   */
  readonly monitoringEnabled?: boolean;
  /**
   * Phase settings-monitor-esg-ack — called after a successful on-
   * demand ACK check so the parent can refresh the submission data.
   * Defaults to `onRetried` if omitted.
   */
  readonly onAckChecked?: () => void;
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

function formatBytes(bytes: number | null | undefined): string | null {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

function daysAgo(iso: string): number {
  const t0 = Date.parse(iso);
  if (!Number.isFinite(t0)) return 0;
  const ms = Date.now() - t0;
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}

interface AckPayloadShape {
  ackType?: string;
  [k: string]: unknown;
}

function parseAckType(payload: string | null | undefined): string | null {
  if (!payload) return null;
  try {
    const parsed = JSON.parse(payload) as AckPayloadShape;
    if (typeof parsed.ackType === 'string' && parsed.ackType.trim() !== '') {
      return parsed.ackType;
    }
    return null;
  } catch {
    return null;
  }
}

function downloadAckPayload(payload: string, submissionId: string | undefined): void {
  const blob = new globalThis.Blob([payload], { type: 'application/json' });
  const url = globalThis.URL.createObjectURL(blob);
  const a = globalThis.document.createElement('a');
  a.href = url;
  a.download = `esg-ack-${submissionId ?? 'submission'}.json`;
  globalThis.document.body.appendChild(a);
  a.click();
  globalThis.document.body.removeChild(a);
  globalThis.URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────
// State derivation
// ─────────────────────────────────────────────────────────────────────

type BannerState = 'awaiting' | 'overdue' | 'acknowledged';

interface DerivedState {
  state: BannerState;
  submittedAt: string | null;
  ackReceivedAt: string | null;
  ackTimestamp: string | null;
  staleAt: string | null;
  ackPayload: string | null;
}

function deriveState(
  submission: Submission | undefined,
  submittedAtProp: string | null | undefined,
  ackPendingProp: boolean,
): DerivedState | null {
  // Pull from submission first; fall back to legacy props.
  const submittedAt = submission?.EsgSubmittedAt ?? submittedAtProp ?? null;
  // The legacy positional API uses `ackPending` (default true). If a
  // caller passes ackPending=false but no submission, treat that as
  // "acknowledged" (back-compat: HEAD's pill displayed "Acknowledged"
  // in that case).
  const ackReceivedAt =
    submission?.EsgAckReceivedAt ??
    (ackPendingProp === false && submittedAt ? submittedAt : null);
  const ackTimestamp = submission?.EsgAckTimestamp ?? null;
  const staleAt = submission?.EsgStaleAt ?? null;
  const ackPayload = submission?.EsgAckPayload ?? null;

  // State 1: nothing transmitted yet → hide banner.
  if (!submittedAt) return null;

  // State 4: ack received (supersedes stale — a late ack wins).
  if (ackReceivedAt) {
    return { state: 'acknowledged', submittedAt, ackReceivedAt, ackTimestamp, staleAt, ackPayload };
  }
  // State 3: stale.
  if (staleAt) {
    return {
      state: 'overdue',
      submittedAt,
      ackReceivedAt: null,
      ackTimestamp: null,
      staleAt,
      ackPayload: null,
    };
  }
  // State 2: awaiting.
  return {
    state: 'awaiting',
    submittedAt,
    ackReceivedAt: null,
    ackTimestamp: null,
    staleAt: null,
    ackPayload: null,
  };
}

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

export function EsgMonitorBanner({
  submission,
  agencyReferenceNumber,
  submissionId,
  submittedAt,
  envelopeId,
  transmissionSizeBytes,
  retryCount,
  lastError,
  ackPending = true,
  onRetried,
  monitoringEnabled = true,
  onAckChecked,
}: EsgMonitorBannerProps): ReactElement | null {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [checkingAck, setCheckingAck] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);

  const derived = deriveState(submission, submittedAt, ackPending);
  if (!derived) return null;

  const { state, submittedAt: derivedSubmittedAt, ackTimestamp, ackReceivedAt, ackPayload } =
    derived;

  // Resolve auxiliary fields, preferring submission over positional props.
  const effectiveEnvelopeId = envelopeId ?? submission?.EsgEnvelopeId ?? null;
  const effectiveSizeBytes =
    transmissionSizeBytes ?? submission?.EsgTransmissionSizeBytes ?? null;
  const effectiveRetryCount = retryCount ?? submission?.EsgRetryCount ?? null;
  const effectiveLastError = lastError ?? submission?.EsgLastError ?? null;
  const effectiveSubmissionId = submissionId ?? submission?.Id ?? null;

  const errorPresent =
    typeof effectiveLastError === 'string' && effectiveLastError.length > 0;
  const hasDetails =
    Boolean(derivedSubmittedAt) ||
    Boolean(effectiveEnvelopeId) ||
    typeof effectiveSizeBytes === 'number' ||
    typeof effectiveRetryCount === 'number';
  const showRetry =
    Boolean(effectiveSubmissionId) &&
    Boolean(derivedSubmittedAt) &&
    (errorPresent || state === 'awaiting');

  const handleCheckAck = async (): Promise<void> => {
    if (!effectiveSubmissionId) return;
    setCheckingAck(true);
    setCheckError(null);
    try {
      await v2.submissions.checkEsgAck(effectiveSubmissionId);
      if (onAckChecked) onAckChecked();
      else if (onRetried) onRetried();
    } catch (err: unknown) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'ACK check failed';
      setCheckError(msg);
    } finally {
      setCheckingAck(false);
    }
  };

  const handleRetry = async (): Promise<void> => {
    if (!effectiveSubmissionId) return;
    setRetrying(true);
    setRetryError(null);
    try {
      await v2.submissions.transmitRetry(effectiveSubmissionId);
      if (onRetried) onRetried();
    } catch (err: unknown) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Retry failed';
      setRetryError(msg);
    } finally {
      setRetrying(false);
    }
  };

  const sizeLabel = formatBytes(effectiveSizeBytes);
  const transmissionLabel =
    typeof effectiveRetryCount === 'number'
      ? `${effectiveRetryCount + 1} (${effectiveRetryCount} retries)`
      : null;
  const ackType = parseAckType(ackPayload);

  // Visual palette per state. Error overrides background to red.
  const background = errorPresent
    ? 'var(--danger, #b91c1c)'
    : state === 'acknowledged'
      ? '#15803D'
      : state === 'overdue'
        ? '#B45309'
        : 'var(--primary)';

  const pillLabel = errorPresent
    ? 'Transmission error'
    : state === 'acknowledged'
      ? 'Acknowledged'
      : state === 'overdue'
        ? 'Acknowledgement overdue'
        : 'Submitted — Awaiting acknowledgement';

  const stateIcon = state === 'acknowledged' ? '✅' : state === 'overdue' ? '⚠' : '📡';

  return (
    <div
      role="status"
      aria-label="ESG monitor"
      data-testid="esg-monitor-banner"
      data-state={state}
      style={{
        background,
        color: '#fff',
        padding: '12px 20px',
        borderRadius: 8,
        marginBottom: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            style={{
              fontSize: 11,
              letterSpacing: 0.08,
              textTransform: 'uppercase',
              opacity: 0.7,
            }}
          >
            ESG monitor
          </span>
          {agencyReferenceNumber && (
            <span style={{ fontFamily: 'monospace', fontSize: 14 }}>
              {agencyReferenceNumber}
            </span>
          )}

          {state === 'awaiting' && (
            <span style={{ fontSize: 12, opacity: 0.85 }}>
              {stateIcon} Submitted to ESG gateway — awaiting acknowledgement
            </span>
          )}
          {state === 'overdue' && (
            <>
              <span style={{ fontSize: 13 }}>
                {stateIcon} Agency acknowledgement overdue — submitted{' '}
                {daysAgo(derivedSubmittedAt!)} day
                {daysAgo(derivedSubmittedAt!) === 1 ? '' : 's'} ago
              </span>
              <span style={{ fontSize: 11, opacity: 0.85 }}>
                Contact your regulatory operations team
              </span>
            </>
          )}
          {state === 'acknowledged' && (
            <>
              <span style={{ fontSize: 13 }}>
                {stateIcon} Agency acknowledged receipt
              </span>
              <span style={{ fontSize: 11, opacity: 0.85 }}>
                ACK received: {formatDateTime(ackTimestamp ?? ackReceivedAt!)}
                {ackType && <> · ACK type: {ackType}</>}
              </span>
            </>
          )}

          {derivedSubmittedAt && (
            <span style={{ fontSize: 11, opacity: 0.7 }}>
              Submitted {formatDateTime(derivedSubmittedAt)}
            </span>
          )}
          {/* Phase compiler-final-ux-polish — "what to do next" guidance
              while the FDA ESG acknowledgement is outstanding. Static
              text — no API call needed. */}
          {state === 'awaiting' && (
            <span
              data-testid="esg-monitor-awaiting-guidance"
              style={{
                fontSize: 11,
                opacity: 0.7,
                marginTop: 4,
                lineHeight: 1.45,
              }}
            >
              Typically 1–4 business days for FDA ACK. Contact your ESG
              gateway provider if no acknowledgement after 5 business days.
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {state === 'acknowledged' && ackPayload && (
            <button
              type="button"
              data-testid="esg-monitor-download-ack"
              onClick={() => downloadAckPayload(ackPayload, effectiveSubmissionId ?? undefined)}
              style={{
                background: 'rgba(255,255,255,0.18)',
                color: '#fff',
                padding: '6px 12px',
                borderRadius: 6,
                border: 'none',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Download ACK payload
            </button>
          )}
          <span
            data-testid="esg-monitor-status-pill"
            style={{
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              padding: '4px 12px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {pillLabel}
          </span>
        </div>
      </div>

      {/* TODO-105 — transmission details panel. */}
      {hasDetails && (
        <dl
          data-testid="esg-monitor-details"
          style={{
            margin: 0,
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 12,
            rowGap: 2,
            fontSize: 12,
            opacity: 0.92,
            background: 'rgba(0,0,0,0.18)',
            padding: '8px 12px',
            borderRadius: 6,
          }}
        >
          {effectiveEnvelopeId && (
            <>
              <dt style={{ opacity: 0.7 }}>Envelope ID</dt>
              <dd
                data-testid="esg-monitor-envelope"
                style={{ margin: 0, fontFamily: 'monospace' }}
              >
                {effectiveEnvelopeId}
              </dd>
            </>
          )}
          {derivedSubmittedAt && (
            <>
              <dt style={{ opacity: 0.7 }}>Transmitted at</dt>
              <dd style={{ margin: 0 }}>{formatDateTime(derivedSubmittedAt)}</dd>
            </>
          )}
          {sizeLabel && (
            <>
              <dt style={{ opacity: 0.7 }}>Package size</dt>
              <dd data-testid="esg-monitor-size" style={{ margin: 0 }}>
                {sizeLabel}
              </dd>
            </>
          )}
          {transmissionLabel && (
            <>
              <dt style={{ opacity: 0.7 }}>Transmission #</dt>
              <dd data-testid="esg-monitor-retry-count" style={{ margin: 0 }}>
                {transmissionLabel}
              </dd>
            </>
          )}
        </dl>
      )}

      {/* TODO-106 — error + retry action. */}
      {errorPresent && (
        <div
          data-testid="esg-monitor-error"
          style={{
            background: 'rgba(0,0,0,0.25)',
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          ⚠ Transmission error: {truncate(effectiveLastError as string, 100)}
        </div>
      )}
      {retryError && (
        <div
          role="alert"
          data-testid="esg-monitor-retry-error"
          style={{
            background: 'rgba(0,0,0,0.3)',
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          Retry failed: {retryError}
        </div>
      )}
      {/* Phase settings-monitor-esg-ack (TODO-074 partial) — monitoring-off
          note + manual Check ACK status button. Only shown while the
          submission is still awaiting an ack and monitoring is disabled. */}
      {!monitoringEnabled && state === 'awaiting' && !errorPresent && (
        <div data-testid="esg-monitor-monitoring-off">
          <div
            style={{
              background: 'rgba(0,0,0,0.20)',
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 12,
              marginBottom: 8,
            }}
          >
            💡 Manual ACK check required — automatic monitoring is disabled
          </div>
          {effectiveSubmissionId && (
            <button
              type="button"
              data-testid="esg-monitor-check-ack-btn"
              onClick={() => {
                void handleCheckAck();
              }}
              disabled={checkingAck}
              style={{
                background: '#fff',
                color: 'var(--primary)',
                border: 'none',
                borderRadius: 6,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: checkingAck ? 'wait' : 'pointer',
                opacity: checkingAck ? 0.7 : 1,
              }}
            >
              {checkingAck ? 'Checking…' : 'Check ACK status'}
            </button>
          )}
          {checkError && (
            <div
              role="alert"
              data-testid="esg-monitor-check-error"
              style={{
                background: 'rgba(0,0,0,0.3)',
                padding: '6px 10px',
                borderRadius: 6,
                fontSize: 12,
                marginTop: 6,
              }}
            >
              Check failed: {checkError}
            </div>
          )}
        </div>
      )}
      {showRetry && (
        <div>
          <button
            type="button"
            data-testid="esg-monitor-retry-btn"
            onClick={() => {
              void handleRetry();
            }}
            disabled={retrying}
            style={{
              background: '#fff',
              color: errorPresent ? 'var(--danger, #b91c1c)' : 'var(--primary)',
              border: 'none',
              borderRadius: 6,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: retrying ? 'wait' : 'pointer',
              opacity: retrying ? 0.7 : 1,
            }}
          >
            {retrying ? 'Retrying…' : 'Retry transmission'}
          </button>
        </div>
      )}
    </div>
  );
}
